import { useRef, useLayoutEffect, useState, useId } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { formatCop } from "../../lib/formatCop.js";
import { cn } from "../../lib/utils.js";

/**
 * Campo de monto en pesos: se ve `$ 25.000` mientras se escribe.
 *
 * Va con `type="text"` y no `type="number"` porque un input numérico SOLO
 * acepta un número crudo como valor — con separadores de miles el navegador lo
 * considera inválido y lo vacía. La contra es que se pierde el spinner nativo,
 * así que las flechas son propias: suben y bajan de `step` en `step` (la cuota
 * diaria de la moto), que es la unidad real del negocio.
 *
 * El valor que viaja hacia afuera son SIEMPRE dígitos pelados ("25000") o
 * cadena vacía: el formato es cosa de la vista y nunca contamina lo que se
 * manda al backend.
 *
 * Con un monto ya escrito, las teclas `+` y `-` abren una CUENTA: el campo
 * muestra `$ 20.000 + 25.000` y encima cuelga el total. Las flechas solo saben
 * de cuotas enteras, y el conductor que manda 20.000 el sábado y la cuota de
 * 25.000 el domingo obligaba a borrar el renglón y escribir 45.000 sacado de
 * cabeza; el `-` corrige sin borrar ("anoté 25.000 y eran 23.000": `-2000`).
 * Hacia afuera viaja el TOTAL desde la primera cifra; Enter o salir del campo
 * cierran la cuenta y queda solo él a la vista. Una cuenta que da negativo no
 * es un monto: viaja vacía, marcada, y se queda abierta y en rojo hasta
 * corregirla.
 *
 * @param {string} value          Dígitos pelados o "".
 * @param {(digits: string, estado: { invalid: boolean }) => void} onChange
 *   `estado.invalid` es true mientras la cuenta da negativo, y ahí `digits`
 *   viaja vacío. Un formulario donde el vacío SIGNIFICA algo ("desembolsá el
 *   total", "sin costo", "no se saca nada") tiene que mirarlo: si no, una resta
 *   de más pasa como ese significado.
 * @param {boolean} [invalid]     La marca `invalid` que el formulario guardó del
 *   último `onChange`, devuelta. Solo hace falta donde el formulario la mira:
 *   si la apaga desde afuera (un reset con el panel abierto), la cuenta en rojo
 *   se descarta en vez de seguir pintada sobre un formulario que ya la olvidó.
 * @param {number} [step]         Salto de las flechas (cuota diaria).
 * @param {boolean} [error]       Pinta el borde de error.
 * @param {string} [upLabel]      Nombre accesible de la flecha de subir. El
 *   default es genérico porque el componente lo es; donde el salto significa
 *   algo del negocio (la planilla sube DE CUOTA EN CUOTA) conviene decirlo.
 * @param {string} [downLabel]    Ídem, la de bajar.
 * @param {object|Function} [inputRef]  Ref al `<input>` de adentro, para que
 *   quien lo usa pueda enfocarlo (la planilla salta acá sola cuando la
 *   búsqueda deja una sola moto).
 */
/** Máximo de Postgres para las columnas `*_cop`, que son INTEGER. */
const MAX_COP = 2147483647;

// Los montos después del primero van sin "$": el primero ya lo lleva, y
// repetirlo en un campo de 130 px le quita el lugar a los números.
const SUMANDO_FORMAT = new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 });

// Separa los montos de una cuenta. Acepta el "−" tipográfico además del "-" del
// teclado: es el que muestra el campo, y vuelve dentro del texto a cada tecla.
const OPERADOR = /([+\-−])/;

/** Una operación recién abierta, todavía sin número: `"+"` o `"-"`. */
const abierta = (term) => term === "+" || term === "-";

/** "75000" → "$ 75.000". Vacío se queda vacío para que el placeholder se vea. */
export function formatMoneyInput(value) {
  const digits = String(value ?? "").replace(/[^0-9]/g, "");
  return digits === "" ? "" : formatCop(Number(digits));
}

/**
 * Texto del campo → los montos de la cuenta, en dígitos pelados.
 *
 * `"$ 20.000 + 25.000 − 2.000"` → `["20000", "+25000", "-2000"]`: el primero va
 * sin signo y los demás con el suyo. Un signo al final, sin número, queda como
 * `"+"` o `"-"` para que la cuenta siga abierta mientras llega el siguiente;
 * uno sin nada a la izquierda se descarta, porque no hay a qué sumarle ni de
 * qué restar. Con dos signos seguidos manda el último.
 *
 * @returns {string[]} vacío = campo vacío; uno = monto suelto; dos o más = cuenta.
 */
export function parseMoneySum(text, { allowZero = false } = {}) {
  // Por defecto "0" es vaciar: en un monto a cobrar, cero y vacío son lo
  // mismo. Con `allowZero` el cero se conserva porque SIGNIFICA algo —la base
  // de la unidad en 0 es "esta unidad no cobra garantía", que no es lo mismo
  // que no haberla configurado.
  const zeros = allowZero ? /^0+(?=\d)/ : /^0+/;
  const limpiar = (chunk) => (chunk ?? "").replace(/\D/g, "").replace(zeros, "");
  const [primero, ...resto] = String(text ?? "").split(OPERADOR);
  const tramos = [["+", limpiar(primero)]];
  for (let i = 0; i < resto.length; i += 2) {
    tramos.push([resto[i] === "+" ? "+" : "-", limpiar(resto[i + 1])]);
  }
  const [opFinal, digitosFinales] = tramos[tramos.length - 1];
  const terms = tramos
    .filter(([, digits]) => digits !== "")
    .map(([op, digits], i) => (i === 0 ? digits : `${op}${digits}`));
  if (terms.length > 0 && tramos.length > 1 && digitosFinales === "") terms.push(opFinal);
  return terms;
}

/** Total de la cuenta (una operación abierta, sin número, no mueve nada). */
export function sumMoneyTerms(terms) {
  return terms.reduce((total, term) => (abierta(term) ? total : total + Number(term)), 0);
}

/** `["20000", "+25000", "-2000"]` → "$ 20.000 + 25.000 − 2.000"; abierta, "$ 20.000 −". */
function formatSum(terms) {
  return terms
    .map((term, i) => {
      if (i === 0) return formatCop(Number(term));
      const op = term[0] === "-" ? "−" : "+";
      const digits = term.slice(1);
      return digits === "" ? op : `${op} ${SUMANDO_FORMAT.format(Number(digits))}`;
    })
    .join(" ");
}

export function MoneyInput({
  value,
  onChange,
  step = 1,
  max = MAX_COP,
  allowZero = false,
  error = false,
  invalid,
  disabled = false,
  placeholder = "",
  className,
  onKeyDown,
  onBlur,
  upLabel = "Subir el monto",
  downLabel = "Bajar el monto",
  inputRef: forwardedRef,
  "aria-describedby": describedBy,
  ...props
}) {
  const inputRef = useRef(null);
  const totalId = useId();
  // La cuenta en curso, o null. Hacia afuera solo viaja el total: los montos
  // sueltos son cosa de la vista mientras se escribe.
  const [terms, setTerms] = useState(null);

  /** Alimenta el ref propio (cursor) y el de afuera (foco) de una sola vez. */
  function attachInput(node) {
    inputRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }
  const raw = value ?? "";

  /** Montos de la cuenta → lo que viaja hacia afuera. */
  function toDigits(list) {
    if (list.length === 0) return "";
    const total = sumMoneyTerms(list);
    // Un total negativo no es un monto: viaja vacío, con `invalid` en el
    // estado del `onChange` para que el formulario no lo lea como un vacío más.
    if (total < 0 || (total === 0 && !allowZero)) return "";
    return String(total);
  }

  // La cuenta se muestra mientras siga describiendo el valor. Si el valor cambió
  // desde afuera (la planilla vacía el renglón al registrar), manda el valor.
  // Una cuenta en negativo emite el mismo vacío que un reset, así que por el
  // valor no se distinguen: ahí decide la marca que devuelve el formulario.
  const enCuenta =
    terms !== null &&
    toDigits(terms) === raw &&
    !(invalid === false && sumMoneyTerms(terms) < 0);
  const totalCuenta = enCuenta ? sumMoneyTerms(terms) : 0;
  // Restar de más deja la cuenta abierta y en rojo. Antes se descartaba la
  // cifra que la volvía negativa: "20.000 − 25000" quedaba en 17.500, un monto
  // creíble que nadie escribió y que el Enter de la planilla registraba.
  const negativo = totalCuenta < 0;
  const display = enCuenta ? formatSum(terms) : raw === "" ? "" : formatCop(Number(raw));
  // La cuenta del globo: recién hay cuenta con dos montos escritos. Con la
  // operación recién abierta ("$ 20.000 +") el globo muestra solo el total.
  const escritos = enCuenta ? terms.filter((term) => !abierta(term)) : [];
  const cuenta = escritos.length > 1 ? formatSum(escritos) : null;

  // Al insertar los puntos, React conserva la posición del cursor POR ÍNDICE y
  // el separador nuevo lo corre un lugar hacia atrás (tipear "25000" terminaba
  // dando "2.5000"). Como acá siempre se escribe agregando al final, el cursor
  // se reancla ahí después de cada render.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (el && document.activeElement === el) {
      const end = el.value.length;
      el.setSelectionRange(end, end);
    }
  }, [display]);

  /** Texto tecleado → dígitos. Sin ceros a la izquierda: "0" es vaciar. */
  function handleChange(text) {
    const next = parseMoneySum(text, { allowZero });
    // Tope duro: las columnas `*_cop` son INTEGER, así que pegar un monto de
    // más dígitos desborda en la base y el backend contesta un 500 que no dice
    // nada. Se rechaza la tecla en vez de aceptar un valor imposible. Vale para
    // CADA monto de la cuenta y no solo para el total: uno de más cifras no
    // existe, y en JS pierde precisión (o da NaN) antes de llegar a la resta.
    const desborda =
      next.some((term) => !abierta(term) && Math.abs(Number(term)) > max) ||
      sumMoneyTerms(next) > max;
    if (desborda) {
      onChange(raw, { invalid: negativo });
      return;
    }
    setTerms(next.length > 1 ? next : null);
    onChange(toDigits(next), { invalid: sumMoneyTerms(next) < 0 });
  }

  function bump(delta) {
    // Una flecha a mitad de una cuenta ajusta el TOTAL y la cierra: es un
    // ajuste de lo que va, no un monto más. Con la cuenta en rojo el total es el
    // de la cuenta, no el vacío que viaja afuera.
    const current = enCuenta ? totalCuenta : Number.parseInt(raw, 10) || 0;
    setTerms(null);
    const next = Math.min(max, Math.max(0, current + delta));
    onChange(next === 0 && !allowZero ? "" : String(next), { invalid: false });
  }

  function handleKeyDown(e) {
    // Con Ctrl, Alt o Cmd la tecla es un atajo (Ctrl + "-" aleja el zoom), no
    // una operación: se deja pasar entera.
    const atajo = e.ctrlKey || e.metaKey || e.altKey;
    if ((e.key === "+" || e.key === "-") && !atajo) {
      // Se intercepta en vez de dejar que el signo entre como texto: con el
      // monto seleccionado (el Tab del navegador lo selecciona entero) la tecla
      // lo REEMPLAZABA y el campo quedaba vacío, y con el cursor al principio
      // quedaba a la izquierda, sin nada a qué sumarle.
      e.preventDefault();
      // Sin monto no hay cuenta que abrir. Una cuenta que dio cero (o negativo)
      // deja el valor vacío pero sigue abierta, y ahí la tecla sí vale.
      if (raw === "" && !enCuenta) return;
      const actuales = enCuenta ? terms : [raw];
      // Recién abierta, la otra tecla la cambia: "+" y enseguida "-" es restar.
      const previos = abierta(actuales[actuales.length - 1]) ? actuales.slice(0, -1) : actuales;
      setTerms([...previos, e.key]);
      return;
    }
    // Las flechas del teclado hacen lo mismo que los botones: es el gesto de
    // quien viene tipeando y no quiere soltar el teclado.
    if (e.key === "ArrowUp") {
      e.preventDefault();
      bump(step);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      // Mismo criterio que el botón de bajar, deshabilitado con el campo
      // vacío: bajar desde "nada" no es un monto. Con `allowZero` la tecla
      // inventaba un 0 que nadie escribió, y en un campo obligatorio ese 0
      // pasaba la validación.
      if (raw !== "") bump(-step);
      return;
    }
    // Enter cierra la cuenta y la tecla SIGUE su camino: el total ya viajó con
    // la última cifra, así que quien registra con Enter (la planilla) cobra el
    // total y no el primer monto. En rojo NO se cierra: quedaría el campo vacío
    // y se perdería la cuenta que hay que corregir.
    if (e.key === "Enter" && !negativo) setTerms(null);
    onKeyDown?.(e);
  }

  function handleBlur(e) {
    // Ídem al salir del campo.
    if (!negativo) setTerms(null);
    onBlur?.(e);
  }

  const chevronCls =
    "flex h-1/2 w-6 items-center justify-center text-text-muted transition-colors " +
    "hover:bg-surface-2 hover:text-text-primary active:bg-surface-3 " +
    "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent";

  return (
    <div
      className={cn(
        // Sin `overflow-hidden`: el total de la cuenta cuelga por fuera del
        // borde y el recorte lo escondía. Las esquinas que recortaba las
        // redondean ahora las propias flechas.
        "relative inline-flex h-10 items-stretch rounded-md border shadow-card transition-colors",
        "bg-surface-1 focus-within:ring-2 focus-within:ring-offset-0",
        error || negativo
          ? "border-danger focus-within:ring-danger/40"
          : "border-border-strong focus-within:border-primary focus-within:ring-primary/40",
        disabled && "opacity-50",
        className,
      )}
    >
      <input
        ref={attachInput}
        type="text"
        // Teclado numérico en el teléfono aunque el campo sea de texto.
        inputMode="numeric"
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={display}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        aria-invalid={error || negativo || undefined}
        // El total se SUMA a lo que ya describía el campo (el hint de `Field`),
        // no lo pisa.
        aria-describedby={[describedBy, enCuenta && totalId].filter(Boolean).join(" ") || undefined}
        className={cn(
          "min-w-0 flex-1 bg-transparent px-3 py-2 text-right text-sm tabular-nums",
          "text-text-primary placeholder:text-text-muted outline-none",
          "disabled:cursor-not-allowed",
        )}
        {...props}
      />
      {/* Columna de flechas: reemplaza al spinner nativo que se pierde con
          `type="text"`, y de paso es la única que se puede estilar. */}
      <div className="flex w-6 shrink-0 flex-col border-l border-border-strong">
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          onClick={() => bump(step)}
          aria-label={upLabel}
          className={cn(chevronCls, "rounded-tr-md border-b border-border-strong")}
        >
          <ChevronUp className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
        </button>
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled || raw === ""}
          onClick={() => bump(-step)}
          aria-label={downLabel}
          className={cn(chevronCls, "rounded-br-md")}
        >
          <ChevronDown className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
        </button>
      </div>
      {/* El total a la vista mientras hay una cuenta abierta: sin él habría que
          hacerla de cabeza igual, que es justo lo que vino a evitar. Lleva la
          cuenta entera y no solo el resultado porque en la planilla el campo
          mide 144 px y "$ 20.000 + 25.000" no entra. En las tarjetas del
          teléfono va solo el total: ahí la celda topa todo lo que tiene adentro
          al ancho del campo (§ Table mobileStack) y la cuenta entera se salía
          del globo.

          Va ARRIBA del campo. La planilla vive en una tabla con
          `overflow-x-auto`, que vuelve `auto` también el eje vertical: con una
          sola fila —la búsqueda que salta al monto— un globo colgando por debajo
          se salía del contenedor, quedaba recortado y aparecía una barra de
          scroll mientras se tipeaba. Arriba siempre hay algo (la cabecera, la
          fila anterior, la etiqueta del Field), y el error y el hint, que van
          debajo, quedan a la vista. */}
      {enCuenta && (
        <span
          id={totalId}
          aria-live="polite"
          className={cn(
            "pointer-events-none absolute bottom-full right-0 z-20 mb-1 whitespace-nowrap rounded-md border bg-surface-2 px-2 py-0.5 text-xs tabular-nums text-text-primary shadow-panel",
            negativo ? "border-danger" : "border-border",
          )}
        >
          {cuenta && <span className="text-text-secondary max-md:hidden">{cuenta} </span>}
          <span className={cn("font-semibold", negativo && "text-danger")}>
            = {formatCop(totalCuenta)}
          </span>
        </span>
      )}
    </div>
  );
}

MoneyInput.displayName = "MoneyInput";
export default MoneyInput;
